/* eslint-disable perfectionist/sort-objects */
/* eslint no-param-reassign: ["error", { "props": false }] */
import type { Element } from 'html-react-parser';
import { isNonEmptyString } from 'tempest-common';
import { safeJSONParse } from '@/utils/json-parse';
import type { ClientSiteConfig } from '@/temporary-site-config';
import type {
	AffiliateTagWithLevel,
	CommerceButtonRenderDetails,
} from '@/types/commerce';
import {
	isCommerceItem,
	isCommerceOffer,
	type CommerceItem,
	type CommerceOffer,
	identifyOfferStore,
	buildCommerceOfferId,
	replaceAmazonTagInUrl,
	type CommerceItemImage,
	disableLinkWrapVendors,
	buildCommerceImageSrcSet,
	type CommerceExternalOffer,
	type CommerceEmbedSharedOptions,
	isBaseCommerceSingleEmbedConfig,
	type BaseCommerceSingleEmbedConfig,
	type CommerceEmbedImageOverrideOptions,
	type BaseCommerceSingleOfferAwareConfig,
} from '@themaven-net/commerce-shared';

export const getImageDetails = (image: CommerceItemImage) => {
	if (image.sizes !== undefined && image.sizes.length !== 0) {
		const srcSet = buildCommerceImageSrcSet(image);
		// Use the largest image as the "src" which will not be immediately displayed
		// due to the "srcset" placeholder but will be exposed for crawlers
		const { height, src, width } = image.sizes[image.sizes.length - 1];

		return { height, src, srcSet, width };
	}

	return { src: image.source };
};

/**
 * Determine the proper button text for an offer
 *
 * This will also replace any data slugs in the text
 * @todo add dynamic token replacement here
 */
export function buildOfferButtonText(
	offer: CommerceOffer,
	options: CommerceEmbedSharedOptions,
	defaultButtonText: string,
): string {
	const offerId = buildCommerceOfferId(offer);

	if (options.buttonTitleByIdentifier === undefined) {
		return defaultButtonText;
	}

	if (isNonEmptyString(options.buttonTitleByIdentifier[offerId])) {
		return options.buttonTitleByIdentifier[offerId];
	}

	if (isNonEmptyString(options.buttonTitleByIdentifier[offer.url])) {
		return options.buttonTitleByIdentifier[offer.url];
	}

	return defaultButtonText;
}

/**
 * Build the button details for a single commerce offer based on the supplied options
 */
export function buildButtonDetails(
	offer: CommerceOffer,
	options: CommerceEmbedSharedOptions,
	defaultButtonText: string,
): CommerceButtonRenderDetails {
	return {
		offer,
		text: buildOfferButtonText(offer, options, defaultButtonText),
	};
}

/**
 * Render an individual offer with the "show price" option enabled
 *
 * @todo this will probably go away with the token replacement method
 * @returns TemplateResult for the offer.
 */
export function renderOfferWithPrice(offer: CommerceOffer): string | undefined {
	const vendorName = identifyOfferStore(offer);

	// // external without a defined store name, render the "without price" version.
	if (vendorName === undefined) {
		return undefined;
	}

	return `${offer.price} from ${vendorName}`;
}

export interface CommerceLinkRequestStatus {
	requestStatus: string;
}

/** @todo move these definitions and typecheck to commerce api? */
export function isCommerceLinkRequestStatus(obj: unknown) {
	if (obj === null || typeof obj !== 'object') {
		return false;
	}

	const status = obj as CommerceLinkRequestStatus;

	return typeof status.requestStatus === 'string';
}

/**
 * Swaps out the tag in a given url or adds one if no tag is present.
 * If the url already has a tag, then it just returns the original url.
 * It sorts the array of affiliateTags by precedence and
 * returns the tag with highest precedence.
 *
 * TODO: Move this to the commerce-api shared package
 * and update logic once we have post level tags
 * @param url
 * @param affiliateTags
 * @returns
 */
export function swapAffiliateTags(
	url: string,
	affiliateTags?: AffiliateTagWithLevel[],
) {
	if (affiliateTags && affiliateTags.length) {
		// Post/Page-level tags have precedence over site-level
		affiliateTags.sort((a, b) => b.level - a.level);
		return replaceAmazonTagInUrl(url, affiliateTags[0].value.tag);
	}

	return url;
}

export function setCommerceButtonOverride<
	OptionsType extends CommerceEmbedSharedOptions,
>(
	options: OptionsType,
	overrideText: string,
	offerId: string = 'default',
): OptionsType {
	const buffer = options ?? {};

	if (options.buttonTitleByIdentifier === undefined) {
		options.buttonTitleByIdentifier = {};
	}

	options.buttonTitleByIdentifier[offerId] = overrideText;

	return buffer;
}

export function buildCommerceItemImage(
	imageUrl: string,
	altText: string | undefined,
	attribution: string | undefined,
	height: number = 0,
	width: number = 0,
	isWebP: boolean = false,
): CommerceItemImage {
	return {
		alt: altText,
		isWebP,
		sizes: [
			{
				height,
				src: imageUrl,
				width,
			},
		],
		source: attribution,
	};
}

export function setCommerceImageOverride<
	OptionsType extends CommerceEmbedImageOverrideOptions,
>(
	options: OptionsType,
	imageUrl: string | undefined,
	altText: string | undefined,
	attibution: string | undefined,
): OptionsType {
	const buffer = options ?? {};

	if (imageUrl)
		if (
			buffer.imageOverrides === undefined ||
			buffer.imageOverrides.length === 0
		) {
			buffer.imageOverrides = [
				buildCommerceItemImage(imageUrl, altText, attibution),
			];
		}

	return buffer;
}

/**
 * This function is here because of poorly formatted data
 * coming out of the CMS.  Remove this after the formatting has
 * been fixed and the old data cleaned up.
 * @deprecated
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildFullExternalItem(parsed: any): CommerceItem | undefined {
	if (
		parsed?.commerceConfig?.item === undefined ||
		isCommerceItem(parsed.commerceConfig.item)
	) {
		return parsed.commerceConfig.item;
	}

	// force the issue.
	const item = parsed.commerceConfig.item as CommerceItem;

	if (item.title === undefined) {
		item.title = parsed.buttonText || parsed.text || 'Commerce Item';
	}

	item.identifiers = item.identifiers ?? {};
	item.images = item.images ?? [];

	const url = item.offers?.[0]?.url;

	if (typeof url === 'string' && !isCommerceOffer(item.offers[0])) {
		item.offers[0] = {
			url,
			vendor: 'External',
			price: '',
			saved: '',
			pullDate: Date.now(),
		} as CommerceExternalOffer;
	}

	return item;
}

/**
 * Build a base commerce embed config from a json blob
 */
export function buildBaseSingleCommerceEmbedFromDom<
	EmbedType extends BaseCommerceSingleEmbedConfig,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
>(
	domNode: Element,
): [Partial<EmbedType>, ReturnType<typeof safeJSONParse>] | undefined {
	const parsed = safeJSONParse(domNode.attribs['data-wp-block']);

	let outcome: Partial<EmbedType> = {};
	let options: Required<EmbedType>['options'] = {};

	if (parsed.commerceConfig === undefined) {
		return undefined;
	}

	if (!isBaseCommerceSingleEmbedConfig(parsed.commerceConfig)) {
		// For poorly formatted extrnal items coming from "Use Link" embeds
		parsed.commerceConfig.item = buildFullExternalItem(parsed);
		if (!isBaseCommerceSingleEmbedConfig(parsed.commerceConfig)) {
			// still no?  bail.
			return undefined;
		}
	}

	if (parsed.imageUrl) {
		options = setCommerceImageOverride(
			parsed.commerceConfig.options,
			parsed.imageUrl,
			parsed.imageAlt ?? '',
			parsed.imageAttribution ?? '',
		);
	}

	if (options === undefined) {
		return parsed.commerceConfig;
	}

	if (parsed.options?.chooseBestOffer !== undefined) {
		options.chooseBestOffer = parsed.options.chooseBestOffer;
	}

	// Infoblock
	if (parsed.buttonText) {
		options = setCommerceButtonOverride(
			options,
			parsed.buttonText,
			parsed.href,
		);
	}

	// CTA
	if (parsed.text) {
		options = setCommerceButtonOverride(options, parsed.text, parsed.href);
	}

	if (parsed.commerceConfig?.item !== undefined) {
		outcome.item = parsed.commerceConfig.item;
	}

	if (parsed.commerceConfig?.offer) {
		(outcome as BaseCommerceSingleOfferAwareConfig).offer =
			parsed.commerceConfig.offer;
	}

	outcome.options = options;

	// true means "use this wrapper"
	// For skimlinks "undefined" === "don't use"
	// For trackonomics "undefined" === "use"
	// @todo see why images do not have skimlinks or trackonomics flags
	outcome = disableLinkWrapVendors(outcome, {
		Skimlinks: !(parsed.skimlinks !== false && parsed.skimlinks !== undefined),
		Trackonomics: !(parsed.trackonomics !== false),
	});

	return [outcome, parsed];
}

export function isCommerceSingleOfferEnabled(
	siteConfig: ClientSiteConfig,
): boolean | undefined {
	const { features2 } = siteConfig;
	const useCommerceSingleOffer = features2.flags['commerce-single-offer'];

	return Boolean(useCommerceSingleOffer);
}
